import React, { FunctionComponent, useEffect, useState } from 'react';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons'; // Import the arrow icon and close icon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { State, UploadType, UploadState, AssetType } from '../types';
import { useNavigateWithinOrg } from '../utils/navigation';
import { useNavigate } from 'react-router-dom';
import { useAuth, useOrganizationList } from '@clerk/clerk-react';

interface AssetUploadStateProps {
    uploadState: UploadState;
    handleClose: (assetId: string) => void;
}

const statetoText = (state: State, assetType: AssetType): string => {
    const displayAssetType =
        assetType === AssetType.PACK_COPY ? 'pack copy' : 'label';
    switch (state) {
        case 'pending':
            return `Queuing ${displayAssetType}...`;
        case 'uploading':
            return `Uploading ${displayAssetType}...`;
        case 'processing':
            return `Processing ${displayAssetType}...`;
        case 'successful':
            return assetType === AssetType.PACK_COPY
                ? 'Pack copy ingest successful.'
                : 'Label check successful.';
        case 'failed':
            return 'Error: Please try reuploading.';
        default:
            return '';
    }
};

const getEstimatedTotalTime = (uploadState: UploadState): number => {
    const { asset_type } = uploadState;
    switch (asset_type) {
        case AssetType.PACK_COPY:
            return 50;
        case AssetType.LABEL:
            return 120;
        default:
            return 50;
    }
};

const AssetUploadState: FunctionComponent<AssetUploadStateProps> = ({
    uploadState,
    handleClose,
}) => {
    const [elapsedTime, setElapsedTime] = useState<number>(0);
    const [isVisible, setIsVisible] = useState(false); // State for animation
    const navigate = useNavigate();
    const navigateWithinOrg = useNavigateWithinOrg();
    const { orgSlug } = useAuth();
    const { setActive, isLoaded } = useOrganizationList();

    const handleView = async () => {
        if (!isLoaded) return;

        handleClose(uploadState.id);

        // Determine the route based on asset_type, including project_id
        let route: string;
        switch (uploadState.asset_type) {
            case AssetType.LABEL:
                route = `/projects/${uploadState.project_id}/labels/${uploadState.id}`;
                break;
            case AssetType.PACK_COPY:
                route = `/projects/${uploadState.project_id}/pack-copies/${uploadState.id}`;
                break;
            default:
                // Fallback to the old behavior for backward compatibility
                route = `/library/${uploadState.id}`;
                break;
        }

        navigateWithinOrg(route);
    };

    useEffect(() => {
        const startTime = uploadState.upload_time * 1000; // Convert to milliseconds

        const updateElapsedTime = () => {
            const currentTime = Date.now();
            const newElapsedTime = currentTime - startTime;
            setElapsedTime(newElapsedTime);
        };

        // Update every 10 milliseconds
        const interval = setInterval(updateElapsedTime, 10);

        // Also update immediately
        updateElapsedTime();

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [uploadState.upload_time]);

    useEffect(() => {
        // Trigger open animation
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50); // Small delay for transition

        return () => clearTimeout(timer);
    }, []);

    const handleAnimatedClose = () => {
        setIsVisible(false); // Trigger close animation
        setTimeout(() => {
            handleClose(uploadState.id); // Call original handleClose after animation
        }, 300); // Match CSS transition duration
    };

    // Calculate progress and etaText based on elapsedTime
    const estimatedTotalTime = getEstimatedTotalTime(uploadState) * 1000; // in milliseconds

    let progress = (elapsedTime / estimatedTotalTime) * 90;
    if (progress > 90) progress = 90;

    // If upload is successful or failed, set progress to 100%
    if (uploadState.state === 'successful' || uploadState.state === 'failed') {
        progress = 100;
    }

    // Calculate ETA only if not in the final state (successful/failed)
    let etaText = '';
    if (uploadState.state !== 'successful' && uploadState.state !== 'failed') {
        let eta = (estimatedTotalTime - elapsedTime) / 1000; // in seconds
        if (eta < 0) eta = 0;

        // Round ETA up to the next 10s step
        const etaStep = Math.ceil(eta / 10) * 10;
        etaText = etaStep > 0 ? `<${etaStep}s` : '<10s';
    }

    // Determine the color of the progress bar based on the upload state
    let progressBarColor = '#634efb'; // default in-progress color
    if (uploadState.state === 'successful') {
        progressBarColor = '#389E0D'; // green when successful
    } else if (uploadState.state === 'failed') {
        progressBarColor = 'red'; // red when failed
    }

    return (
        <div className={`upload-progress-box ${isVisible ? 'open' : ''}`}>
            <div className="close-button" onClick={handleAnimatedClose} />
            <div className="right-container">
                <div className="info-container">
                    <span>
                        <p className="asset-title">{uploadState.asset_name}</p>
                        {uploadState.state === 'successful' && (
                            <button
                                className="view-button"
                                onClick={handleView}
                            >
                                View <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        )}
                    </span>
                    <div className="progress-box">
                        <div className="progress-info">
                            <p className="status-text">
                                {statetoText(
                                    uploadState.state,
                                    uploadState.asset_type,
                                )}
                            </p>
                            {uploadState.state !== 'successful' &&
                                uploadState.state !== 'failed' && (
                                    <div className="eta-info">
                                        <FontAwesomeIcon icon={faClock} />
                                        <p className="eta-text">{etaText}</p>
                                    </div>
                                )}
                        </div>
                        <div className="progress-bar">
                            <div
                                className="progress-color"
                                style={{
                                    width: `${progress}%`,
                                    backgroundColor: progressBarColor,
                                }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssetUploadState;
